import { useState } from 'react';
import { X, Mail, Printer, Send, Loader2, CheckCircle2, AlertCircle, ExternalLink, Zap } from 'lucide-react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Field } from './ui/Field';
import { formatBRL, formatDateBR, monthYearLongBR, formatDateLongBR, driveImageUrl, validateEmailList } from '../lib/formatters';
import { ESPELHO_THEMES } from '../lib/constants';
import { sendEspelhoEmail } from '../lib/email';
import { logEvento, EVENTOS } from '../lib/audit';
import { openMailtoEspelho } from '../lib/email-text';
import { supabase } from '../lib/supabase';

// Apos reenvio do espelho corrigido, zera os campos de aprovacao/recusa
// porque e uma nova rodada de revisao. Historico fica preservado na
// trilha de auditoria (solicitacao_eventos). Best-effort: warning em
// erro mas nao bloqueia o sucesso do envio.
async function clearAprovacaoState(solicitacaoId) {
  if (!solicitacaoId) return;
  try {
    const { error } = await supabase
      .schema('nf')
      .from('solicitacoes')
      .update({
        aprovado_em: null,
        aprovado_por_email: null,
        rejeitado_em: null,
        rejeitado_por_email: null,
        motivo_rejeicao: null,
      })
      .eq('id', solicitacaoId);
    if (error) {
      console.warn('[reenvio] falha ao limpar estado de aprovacao:', error);
    }
  } catch (err) {
    console.warn('[reenvio] excecao ao limpar estado de aprovacao:', err);
  }
}

const EMISSAO_RECIPIENT = 'contasareceber@etus.com.br';

const ALIQUOTA_MAP = {
  ISS:    'aliquota_iss',
  PIS:    'aliquota_pis',
  COFINS: 'aliquota_cofins',
  CSLL:   'aliquota_csll',
  IR:     'aliquota_ir',
  INSS:   'aliquota_inss',
};

const fmtPct = (n) =>
  Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

function calcularRetencoes(solicitacao, servico) {
  const valorBruto = Number(solicitacao.valor) || 0;
  const retidos = (solicitacao.impostos_retidos || []).filter(r => r !== 'Sem retenção');

  const linhas = retidos.map(nome => {
    const col = ALIQUOTA_MAP[nome];
    const raw = col ? servico?.[col] : null;
    const cadastrada = raw !== null && raw !== undefined && raw !== '';
    const aliquota = cadastrada ? Number(raw) : 0;
    const valor = cadastrada ? (aliquota / 100) * valorBruto : 0;
    return { nome, aliquota, cadastrada, valor };
  });

  const totalRetido = linhas.reduce((a, l) => a + l.valor, 0);
  const valorLiquido = valorBruto - totalRetido;

  return { linhas, totalRetido, valorLiquido, valorBruto, hasRetencoes: linhas.length > 0 };
}

export function PDFEspelho({
  open, onClose, solicitacao, empresa, tomador, servico, onToast,
  // Quando true, desabilita (nao esconde) o botao 'Enviar por e-mail'
  // que abre o modal de envio do espelho pro tomador. Usado em replicas
  // que ainda nao mandam espelho pra cliente externo (ex: Supersonic).
  disableEspelhoEmail = false,
  // Overrides por replica do envio do espelho. Se vazios, server cai no
  // default (FROM=RESEND_FROM da env; subject="Solicitação de NF · ...").
  emailFromName = '',          // ex: 'FATURAMENTO BHAZ' — vira o display
                                // name; o endereco fica o do RESEND_FROM.
  emailSubjectPrefix = '',     // ex: 'Nota fácil BHAZ' — substitui o
                                // "Solicitação de NF" no inicio do subject.
  // Liga a caixa amarela de aviso sobre forma de pagamento (PDF + email).
  // So aparece quando a replica explicitamente pediu (hoje so BHAZ).
  showAvisoFormaPagamento = false,
  // Slug da replica ativa (ex: 'bhaz'). Server le RESEND_FROM_<SLUG> e usa
  // como endereco base do remetente quando a env existe — assim BHAZ pode
  // ter dominio proprio verificado no Resend sem mudanca de codigo.
  replicaSlug = '',
  // BHAZ: usa PI da solicitacao em vez do codigo OS-XXXXXXXX no subject
  // do email do espelho (buildEspelhoEmailSubject).
  usePIInSubject = false,
}) {
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [emissaoSending, setEmissaoSending] = useState(false);

  if (!solicitacao) return null;

  // Quando salvar PDF / imprimir, troca o document.title temporariamente
  // (a maioria dos browsers usa o title como nome default do arquivo).
  const handlePrint = () => {
    const empNome = empresa?.nome_fantasia || empresa?.razao_social || '';
    const safe = String(empNome).replace(/[\\/:*?"<>|]/g, '').trim();
    const fileTitle = safe ? `Espelho NF ${safe}` : 'Espelho NF';

    const original = document.title;
    document.title = fileTitle;
    // Restaura depois — o browser ja capturou o title no momento do print()
    setTimeout(() => { document.title = original; }, 1500);

    window.print();
  };

  const openEmailModal = () => {
    setRecipients(solicitacao.emails_envio || tomador?.email || '');
    setSendResult(null);
    setEmailModalOpen(true);
  };

  const handleOpenMailClient = () => {
    if (!recipients.trim()) return;
    if (!validateEmailList(recipients)) {
      setSendResult({ ok: false, msg: 'Algum e-mail está inválido.' });
      return;
    }
    const list = recipients.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
    openMailtoEspelho({ solicitacao, empresa, tomador, servico, to: list });
    setSendResult({ ok: true, msg: 'Aberto no seu cliente de e-mail. Revise e clique Enviar.' });
    if (solicitacao?.id) {
      logEvento(solicitacao.id, EVENTOS.ESPELHO_ENVIADO_MAILTO, { destinatarios: list });
    }
  };

  // Envia direto pra cobranca (contasareceber@etus.com.br) sem precisar
  // aprovacao do tomador. Mesmo HTML do espelho, mas o destinatario unico
  // e a equipe de cobranca pra emissao.
  const handleSendForEmission = async () => {
    if (emissaoSending) return;
    if (!window.confirm(`Enviar este espelho diretamente para ${EMISSAO_RECIPIENT} para emissão da NF? Esta ação não passa pela aprovação do tomador.`)) return;

    setEmissaoSending(true);
    try {
      const r = await sendEspelhoEmail({
        solicitacao, empresa, tomador, servico,
        to: [EMISSAO_RECIPIENT],
        fromName: emailFromName,
        subjectPrefix: emailSubjectPrefix,
        showAvisoFormaPagamento,
        replicaSlug,
        usePIInSubject,
      });
      onToast?.(`Enviado para emissão (${r.to.join(', ')})`, 'ok');
      if (solicitacao?.id) {
        // Limpa estado de aprovacao/recusa porque e um novo envio.
        await clearAprovacaoState(solicitacao.id);
        logEvento(solicitacao.id, EVENTOS.ESPELHO_ENVIADO_SERVIDOR, {
          destinatarios: r.to,
          para_emissao: true,
        });
      }
    } catch (err) {
      onToast?.(`Falha no envio: ${err?.message || 'erro desconhecido'}`, 'error');
    } finally {
      setEmissaoSending(false);
    }
  };

  const handleSendViaServer = async () => {
    if (!recipients.trim()) return;
    if (!validateEmailList(recipients)) {
      setSendResult({ ok: false, msg: 'Algum e-mail está inválido.' });
      return;
    }
    setSending(true);
    setSendResult(null);
    try {
      const list = recipients.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
      const r = await sendEspelhoEmail({
        solicitacao, empresa, tomador, servico, to: list,
        fromName: emailFromName,
        subjectPrefix: emailSubjectPrefix,
        showAvisoFormaPagamento,
        replicaSlug,
        usePIInSubject,
      });
      setSendResult({ ok: true, msg: `Enviado para ${r.to.join(', ')}.` });
      if (solicitacao?.id) {
        // Limpa estado de aprovacao/recusa porque e um novo envio do
        // espelho (corrigido ou nao). Tomador podera aprovar/recusar
        // novamente sem a checagem de idempotencia engasgar.
        await clearAprovacaoState(solicitacao.id);
        logEvento(solicitacao.id, EVENTOS.ESPELHO_ENVIADO_SERVIDOR, {
          destinatarios: r.to,
        });
      }
    } catch (err) {
      setSendResult({ ok: false, msg: err?.message || 'Falha no envio.' });
    } finally {
      setSending(false);
    }
  };

  // Paleta do espelho: cada empresa tem sua cor (default = azul)
  const theme = ESPELHO_THEMES[empresa?.tema_cor] || ESPELHO_THEMES.azul;

  const dataRow = (label, value) => (
    <tr>
      <td style={{
        padding: '7px 12px', fontSize: 9, textTransform: 'uppercase', letterSpacing: '.08em',
        color: '#6b7280', fontWeight: 600, width: 180, verticalAlign: 'top',
        background: '#f7f7f5', borderBottom: '1px solid #e8e6e1',
      }}>{label}</td>
      <td style={{
        padding: '7px 12px', fontSize: 11.5,
        verticalAlign: 'top', borderBottom: '1px solid #e8e6e1',
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}>{value || '—'}</td>
    </tr>
  );

  const inscricoes = [
    empresa?.inscricao_estadual && `IE ${empresa.inscricao_estadual}`,
    empresa?.inscricao_municipal && `IM ${empresa.inscricao_municipal}`,
  ].filter(Boolean).join(' · ');

  const Box = ({ title, e }) => {
    // Entidade do exterior (LLCs, GOOGLE LLC, etc): em vez do endereco
    // estruturado BR e do CNPJ, mostra a unica linha de endereco do
    // exterior + o identificador opcional (que pode ser EIN/VAT, ou vazio).
    if (e?.is_exterior) {
      return (
        <div style={{ border: '1px solid #e8e6e1', borderRadius: 8, padding: 14 }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, color: theme.main, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>
            {title}
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.25 }}>{e?.razao_social || '—'}</div>
          <div style={{ fontSize: 11, color: '#3f4548', marginTop: 4, lineHeight: 1.5 }}>
            {e?.endereco_exterior ? <>{e.endereco_exterior}<br /></> : null}
            {e?.cnpj
              ? <span className="font-mono" style={{ fontSize: 10.5 }}>ID {e.cnpj}</span>
              : null}
          </div>
        </div>
      );
    }
    // Entidade nacional: endereco estruturado + CNPJ (formato original).
    // Se o documento tem 11 digitos, e Pessoa Fisica — troca o label pra CPF.
    const docDigits = String(e?.cnpj || '').replace(/\D/g, '').length;
    const docLabel = docDigits === 11 ? 'CPF' : 'CNPJ';
    return (
      <div style={{ border: '1px solid #e8e6e1', borderRadius: 8, padding: 14 }}>
        <div style={{ fontSize: 9.5, fontWeight: 700, color: theme.main, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>
          {title}
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.25 }}>{e?.razao_social || '—'}</div>
        <div style={{ fontSize: 11, color: '#3f4548', marginTop: 4, lineHeight: 1.5 }}>
          {e?.logradouro || e?.numero ? <>{e?.logradouro}, {e?.numero} — {e?.bairro}<br /></> : null}
          {e?.cidade || e?.uf ? <>{e?.cidade}/{e?.uf} — CEP {e?.cep}<br /></> : null}
          <span className="font-mono" style={{ fontSize: 10.5 }}>{docLabel} {e?.cnpj || '—'}</span>
        </div>
      </div>
    );
  };

  const ret = calcularRetencoes(solicitacao, servico);

  const linhasCadastradas = ret.linhas.filter(l => l.cadastrada);

  const liquidoCell = (
    <strong className="font-money" style={{ color: theme.dark }}>
      {formatBRL(ret.valorLiquido)}
    </strong>
  );

  return (
    <>
    <Modal open={open} onClose={onClose} title="Espelho da nota fiscal" size="xl"
      subtitle={`ID ${solicitacao.codigo_unico} · ${empresa?.nome_fantasia || empresa?.razao_social || '—'} → ${tomador?.nome_fantasia || tomador?.razao_social || '—'}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} icon={X}>Fechar</Button>
          <Button
            variant="secondary"
            icon={emissaoSending ? Loader2 : Zap}
            onClick={handleSendForEmission}
            disabled={emissaoSending}
            title={`Envia direto para ${EMISSAO_RECIPIENT} (sem passar pelo tomador)`}
          >
            {emissaoSending ? 'Enviando…' : 'Enviar para emissão'}
          </Button>
          <Button
            variant="secondary"
            icon={Mail}
            onClick={openEmailModal}
            disabled={disableEspelhoEmail}
            title={disableEspelhoEmail
              ? 'Envio do espelho por e-mail está desativado nesta réplica.'
              : 'Enviar este espelho por e-mail pro tomador'}
          >
            Enviar por e-mail
          </Button>
          <Button icon={Printer} onClick={handlePrint}>Imprimir / Salvar PDF</Button>
        </>
      }
    >
      <div className="pdf-print" style={{
        background: '#fff', padding: 36, color: '#14171a',
        fontFamily: 'Inter, system-ui, sans-serif',
        border: '1px solid #e8e6e1', borderRadius: 8,
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          paddingBottom: 18, borderBottom: `2px solid ${theme.main}`, marginBottom: 20,
        }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            {empresa?.logo_url ? (
              <img src={driveImageUrl(empresa.logo_url)} alt="" referrerPolicy="no-referrer" style={{
                width: 60, height: 60, objectFit: 'contain', background: '#f7f7f5',
                borderRadius: 8, padding: 6, border: '1px solid #e8e6e1',
              }} onError={e => e.currentTarget.style.display = 'none'} />
            ) : (
              <div style={{
                width: 60, height: 60, borderRadius: 8, background: theme.main,
                color: '#fff', fontWeight: 800, fontSize: 18, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}>{(empresa?.nome_fantasia || 'NF').slice(0, 2).toUpperCase()}</div>
            )}
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, color: theme.main, letterSpacing: '.12em', textTransform: 'uppercase' }}>
                Espelho · solicitação de NF
              </div>
              <div className="font-display" style={{
                fontSize: 22, fontWeight: 600, marginTop: 4, lineHeight: 1.1, letterSpacing: '-0.01em',
              }}>{empresa?.razao_social || '—'}</div>
              {inscricoes && (
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{inscricoes}</div>
              )}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 9.5, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 600 }}>
              ID da solicitação
            </div>
            <div className="font-mono" style={{ fontSize: 14, fontWeight: 600, marginTop: 2 }}>
              {solicitacao.codigo_unico}
            </div>
            {solicitacao.pi && (
              <>
                <div style={{ fontSize: 9.5, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 600, marginTop: 10 }}>
                  PI
                </div>
                <div className="font-mono" style={{ fontSize: 12, fontWeight: 600 }}>
                  {solicitacao.pi}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Prestador + Tomador */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
          <Box title="Prestador" e={empresa} />
          <Box title="Tomador"   e={tomador} />
        </div>

        {/* Intermediário (somente se existir) */}
        {solicitacao.possui_intermediario && (
          <div style={{
            border: '1px solid #e8e6e1', borderRadius: 8, padding: 14, marginBottom: 16,
          }}>
            <div style={{ fontSize: 9.5, fontWeight: 700, color: theme.main, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>
              Intermediário
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.25 }}>
              {solicitacao.intermediario_razao_social || '—'}
            </div>
            <div style={{ fontSize: 11, color: '#3f4548', marginTop: 4 }}>
              <span className="font-mono" style={{ fontSize: 10.5 }}>
                CNPJ {solicitacao.intermediario_cnpj || '—'}
              </span>
            </div>
          </div>
        )}

        {/* Headline: valor bruto + competência */}
        <div style={{
          background: theme.soft, border: `1px solid ${theme.line}`, borderRadius: 8,
          padding: '14px 18px', display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 16,
        }}>
          <div>
            <div style={{ fontSize: 10, color: theme.dark, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>
              Valor bruto
            </div>
            <div className="font-money" style={{
              fontSize: 28, fontWeight: 700, color: theme.dark, marginTop: 2,
            }}>
              {formatBRL(solicitacao.valor)}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 10, color: theme.dark, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>
              Competência
            </div>
            <div style={{ fontSize: 14, fontWeight: 600, marginTop: 2, textTransform: 'capitalize' }}>
              {monthYearLongBR(solicitacao.data_prestacao_servico)}
            </div>
          </div>
        </div>

        {/* Detalhes */}
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #e8e6e1', borderRadius: 8, overflow: 'hidden' }}>
          <tbody>
            {dataRow('Data da solicitação', formatDateBR(solicitacao.data_solicitacao))}
            {dataRow('Serviço', servico ? `${servico.codigo} — ${servico.descricao}` : '—')}
            {dataRow('Descrição', solicitacao.descricao || '—')}
            {linhasCadastradas.map(l => dataRow(
              `${l.nome === 'ISS' ? 'ISSQN' : l.nome} · ${fmtPct(l.aliquota)}`,
              <span className="font-money">{formatBRL(l.valor)}</span>
            ))}
            {linhasCadastradas.length > 0 && dataRow('Valor líquido', liquidoCell)}
            {dataRow('Informações complementares', solicitacao.informacoes_complementares)}
            {dataRow('Previsão de recebimento', solicitacao.previsao_recebimento
              ? formatDateBR(solicitacao.previsao_recebimento)
              : '—')}
            {dataRow('E-mails para envio', solicitacao.emails_envio)}
          </tbody>
        </table>

        {/* Assinaturas */}
        <div style={{ marginTop: 26, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <div style={{ borderTop: '1px solid #14171a', paddingTop: 6, fontSize: 11, color: '#6b7280' }}>
              Solicitante (Comercial)
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>{solicitacao.solicitante}</div>
          </div>
          <div>
            <div style={{ borderTop: '1px solid #14171a', paddingTop: 6, fontSize: 11, color: '#6b7280' }}>
              Validação e aceite
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, marginTop: 2 }}>____________________________</div>
          </div>
        </div>

        {/* Aviso jurídico-tributário unificado (sem cores). Quando a replica
            ativa showAvisoFormaPagamento, ganha um pedaco extra no final
            falando especificamente da forma de pagamento. */}
        <div style={{
          marginTop: 22, paddingTop: 12, borderTop: '1px dashed #d6d3cd',
          fontSize: 10, color: '#3f4548', textAlign: 'justify', lineHeight: 1.55,
        }}>
          Quaisquer solicitações de alteração — incluindo correções de valor, escopo,
          dados cadastrais{showAvisoFormaPagamento
            ? ', retenções tributárias ou forma de pagamento'
            : ' ou retenções tributárias'} — deverão ser apresentadas até o
          5º (quinto) dia do mês subsequente ao da competência da nota fiscal.
          Decorrido esse prazo, <strong>não serão admitidos cancelamentos nem emissões
          substitutivas</strong>, em razão do recolhimento prévio dos tributos incidentes
          (PIS, COFINS, ISS, IRRF, CSLL e demais retenções devidas) e do encerramento
          da apuração fiscal do período. Eventuais divergências identificadas após esse
          prazo deverão ser tratadas comercialmente entre as partes, sem reflexo
          direto na nota já escriturada.
          {showAvisoFormaPagamento && (
            <>
              {' '}
              <strong>Atenção:</strong> a forma de pagamento descrita neste espelho
              será refletida na nota fiscal emitida e, uma vez aprovada,
              <strong> não poderá ser alterada</strong> — recomendamos validar
              essa informação junto à área financeira antes da aprovação,
              evitando transtornos operacionais.
            </>
          )}
        </div>

        {/* Cidade + data */}
        <div style={{
          marginTop: 18, fontSize: 11, color: '#14171a', textAlign: 'right', fontWeight: 500,
        }}>
          {empresa?.cidade || '—'}, {formatDateLongBR(solicitacao.data_solicitacao)}.
        </div>
      </div>
    </Modal>

    <Modal
      open={emailModalOpen}
      onClose={() => !sending && setEmailModalOpen(false)}
      title="Enviar espelho por e-mail"
      subtitle={`Solicitação ${solicitacao.codigo_unico}`}
      footer={
        <>
          <Button variant="secondary" disabled={sending} onClick={() => setEmailModalOpen(false)}>Cancelar</Button>
          <Button
            variant="secondary"
            icon={ExternalLink}
            onClick={handleOpenMailClient}
            disabled={sending || !recipients.trim()}
          >
            Abrir no meu e-mail
          </Button>
          <Button
            icon={sending ? Loader2 : Send}
            onClick={handleSendViaServer}
            disabled={sending || !recipients.trim()}
          >
            {sending ? 'Enviando…' : 'Enviar pelo servidor'}
          </Button>
        </>
      }
    >
      <Field
        label="Destinatários"
        helper="Separe múltiplos e-mails por vírgula."
      >
        <input
          className="input"
          type="text"
          value={recipients}
          onChange={e => setRecipients(e.target.value)}
          placeholder="financeiro@empresa.com, fiscal@empresa.com"
          disabled={sending}
        />
      </Field>

      <div style={{
        marginTop: 12, fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.55,
        padding: '10px 12px', background: 'var(--surface-2)',
        border: '1px solid var(--border)', borderRadius: 8,
      }}>
        <strong>Abrir no meu e-mail</strong> — abre seu Gmail/Outlook com tudo preenchido. Você revisa e clica Enviar. Vai do seu próprio e-mail.
        <br /><br />
        <strong>Enviar pelo servidor</strong> — envia automaticamente do remetente configurado no servidor. Requer o domínio dono verificado no Resend.
      </div>

      {sendResult && (
        <div style={{
          marginTop: 12, fontSize: 12.5, padding: '10px 12px', borderRadius: 8,
          background: sendResult.ok ? 'var(--primary-soft)' : 'var(--danger-soft)',
          color: sendResult.ok ? 'var(--primary)' : 'var(--danger)',
          border: `1px solid ${sendResult.ok ? 'var(--primary-line)' : '#fda29b'}`,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {sendResult.ok ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
          <span>{sendResult.msg}</span>
        </div>
      )}
    </Modal>
    </>
  );
}
